import React from 'react';
import { Shape, Group } from 'react-konva';
import { darken } from 'polished';

interface BarSegment {
  start: number; // Start position in pixels
  width: number; // Width in pixels
  rate: number; // Assignment rate for this segment
  isBillable?: boolean; // New field to indicate if the segment is billable
}

interface BarShapeProps {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  segments: BarSegment[]; // Array of segments with different rates
  onClick?: () => void;
}

/**
 * A custom Konva shape that displays a bar with multiple segments representing different assignment rates.
 * Each segment can have its own rate where:
 * - 0 to 1 represents 0% to 100% fill.
 * - Values greater than 1 represent overfill (up to 200%).
 */
export const BarShape: React.FC<BarShapeProps> = ({
  x,
  y,
  width,
  height,
  color,
  segments,
  onClick
}) => {
  // Get border color (darker version of fill color)
  const borderColor = darken(0.2, color);

  return (
    <Group x={x} y={y}>
      <Shape
        sceneFunc={(context, shape) => {
          // Draw the outer rectangle (full bar) - draw only once
          context.beginPath();
          context.rect(0, 0, width, height);
          context.closePath();
          
          // Fill with light background
          context.fillStyle = 'rgba(230, 230, 240, 0.6)';
          context.fill();
          
          // Draw each segment with its own rate
          segments.forEach(segment => {
            // Normalize assignment rate to be between 0 and 2 (0% to 200%)
            const normalizedRate = Math.min(Math.max(segment.rate, 0), 2);
            // Calculate primary fill height (0% to 100%)
            const primaryFillPercentage = Math.min(normalizedRate, 1);
            // Calculate secondary fill height (100% to 200%)
            const secondaryFillPercentage = normalizedRate > 1 ? normalizedRate - 1 : 0;
            
            // Fill the primary portion based on assignment rate from bottom to top
            context.fillStyle = color;
            const primaryFillHeight = height * primaryFillPercentage;
            
            if (primaryFillHeight > 0) {
              context.fillRect(
                segment.start, 
                height - primaryFillHeight, 
                segment.width, 
                primaryFillHeight
              );
            }
            
            // Fill the secondary portion if assignment rate exceeds 100%
            if (secondaryFillPercentage > 0) {
              context.fillStyle = '#EF5350';
              const secondaryFillHeight = height * secondaryFillPercentage;
              context.fillRect(
                segment.start, 
                height - secondaryFillHeight, 
                segment.width, 
                secondaryFillHeight
              );
            }
          });
          
          // Add stroke to the main rectangle
          context.strokeStyle = borderColor;
          context.lineWidth = 1;
          context.stroke();
          
          // Draw non-billable indicators after stroke is applied
          // This ensures they appear on top without affecting the main rectangle's stroke
          segments.forEach(segment => {
            if (segment.isBillable === false) {
              const indicatorSize = 15; // Size of the indicator triangle
              
              // Draw white triangular background
              context.beginPath();
              context.moveTo(segment.start + segment.width -1, 1); // Top right corner
              context.lineTo(segment.start + segment.width - indicatorSize, 1); // Top left of triangle
              context.lineTo(segment.start + segment.width - indicatorSize + 5, indicatorSize); // Bottom right of triangle
              context.lineTo(segment.start + segment.width -1, indicatorSize); // Bottom right of triangle
              context.closePath();
              
              context.fillStyle = '#FFFFFF'; // White fill
              context.fill();
              
              // Draw dollar sign indicator
              context.font = 'bold 11px Arial';
              context.fillStyle = '#F97316'; // Orange color
              context.fillText('$', segment.start + segment.width - 9, 11);
            }
          });
        }}
        onClick={onClick}
      />
    </Group>
  );
};
